import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";

function fmt(v: number): string {
  const abs = Math.abs(v);
  const prefix = v < 0 ? "-$" : "$";
  if (abs >= 1000) return `${prefix}${(abs / 1000).toFixed(1)}k`;
  return `${prefix}${abs.toFixed(0)}`;
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground p-4">
      {label}
    </div>
  );
}

export default function OutsideSessionWidget() {
  const { analyticsData, isDark, t } = useDashboardData();

  if (!analyticsData) {
    return (
      <Card className="h-full">
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  const os = analyticsData.sessionStats.outsideSession;

  if (os.tradeCount === 0 && !analyticsData.sessionStats.hasEnoughData) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader
          label={t.analytics.sessionsLabel ?? "Sessions"}
          title={t.analytics.outsideSessionTitle}
          isDark={isDark}
          inline
        />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.outsideSessionNoSessions} />
        </CardContent>
      </Card>
    );
  }

  if (os.tradeCount === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader
          label={t.analytics.sessionsLabel ?? "Sessions"}
          title={t.analytics.outsideSessionTitle}
          isDark={isDark}
          inline
        />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  const cellClass = `rounded-md px-2.5 py-1.5 ${isDark ? "bg-muted/40" : "bg-muted/40"}`;
  const labelClass = "text-[9px] font-semibold tracking-widest uppercase text-muted-foreground mb-0.5 leading-tight";

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.analytics.sessionsLabel ?? "Sessions"}
        title={t.analytics.outsideSessionTitle}
        isDark={isDark}
        inline
        compact
      />
      <CardContent className="flex-1 px-3 pb-3 pt-1.5">
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
          <div className={cellClass}>
            <p className={labelClass}>{t.analytics.outsideSessionTrades}</p>
            <p className="text-lg font-bold font-mono leading-tight">{os.tradeCount}</p>
            <p className="text-[10px] text-muted-foreground leading-tight">
              {os.pctOfTotalTrades}% {t.analytics.outsideSessionPctTrades}
            </p>
          </div>
          <div className={cellClass}>
            <p className={labelClass}>{t.analytics.outsideSessionWinRate}</p>
            <p className={`text-lg font-bold font-mono leading-tight ${os.winRate >= 50 ? "text-primary" : "text-destructive"}`}>
              {os.winRate}%
            </p>
            <p className="text-[10px] text-muted-foreground leading-tight">
              {os.winCount}W / {os.lossCount}L
            </p>
          </div>
          <div className={cellClass}>
            <p className={labelClass}>{t.analytics.outsideSessionPnl}</p>
            <p className={`text-lg font-bold font-mono leading-tight ${os.totalPnl > 0 ? "text-warning" : os.totalPnl < 0 ? "text-destructive" : ""}`}>
              {fmt(os.totalPnl)}
            </p>
            <p className="text-[10px] text-muted-foreground leading-tight">
              {t.analytics.outsideSessionAvgPnl}: {fmt(os.avgPnl)}
            </p>
          </div>
          <div className={cellClass}>
            <p className={labelClass}>{t.analytics.outsideSessionProfitFactor}</p>
            <p className={`text-lg font-bold font-mono leading-tight ${os.profitFactor >= 1 ? "text-primary" : "text-destructive"}`}>
              {os.profitFactor === Infinity ? "∞" : os.profitFactor.toFixed(2)}
            </p>
            <p className="text-[10px] text-muted-foreground leading-tight">
              {t.analytics.outsideSessionPctPnl}: {os.pctOfTotalPnl}%
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
